import clsx from "clsx";
import { Button, ButtonGroup } from "flowbite-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import LoadingSpinner from "../../../../externalComponents/LoadingSpinner";
import AutocompleteInput from "../../../../externalComponents/tailwind/autocomplete-input";
import { Badge } from "../../../../externalComponents/tailwind/badge";
import { Field, Label } from "../../../../externalComponents/tailwind/fieldset";
import { Text } from "../../../../externalComponents/tailwind/text";
import { campaignSegmentType } from "./campaign-segment.const";
import { stepCampaignEnum } from "./send-campaign-step.enum";

const SendCampaignRecipients = ({
  className,
  api,
  apiKeyHubspot,
  customerId,
  integrations,
  form,
  setForm,
  selectedContacts,
}) => {
  const navigate = useNavigate();

  const updateSegmentType = useCallback(
    (type) => {
      setForm((_form) => {
        return {
          ..._form,
          type,
          klaviyoSegmentId: null,
          brevoSegmentId: null,
          shopifySegmentQuery: null,
          hubspotListId: null,
          simioSegmentId: null,
          selectedContacts: [],
          selectedOptions: [],
          contactsBreakdown: null,
          optinsContacts: [],
          notOutputsContacts: [],
          inputValue: "",
        };
      });
    },
    [form.type],
  );

  const showLoader = useCallback(
    (fetchingContacts) => {
      setForm((_form) => {
        return { ..._form, fetchingContacts };
      });
    },
    [form.fetchingContacts],
  );

  const updateKlaviyoSegmentId = useCallback(
    (klaviyoSegmentId) => {
      setForm((_form) => {
        return { ..._form, klaviyoSegmentId };
      });
    },
    [form.klaviyoSegmentId],
  );

  const updateBrevoSegmentId = useCallback(
    (brevoSegmentId) => {
      setForm((_form) => {
        return { ..._form, brevoSegmentId };
      });
    },
    [form.brevoSegmentId],
  );

  const updateHubspotListId = useCallback(
    (hubspotListId) => {
      setForm((_form) => {
        return { ..._form, hubspotListId };
      });
    },
    [form.hubspotListId],
  );

  const updateSimioSegmentId = useCallback(
    (simioSegmentId) => {
      setForm((_form) => {
        return { ..._form, simioSegmentId };
      });
    },
    [form.simioSegmentId],
  );

  const updateSelectedContacts = useCallback(
    async (selectedContacts) => {
      const step = stepCampaignEnum.sendingMode;
      setForm((_form) => {
        return {
          ..._form,
          selectedContacts,
          stepToFill: _form.stepToFill < step ? step : _form.stepToFill,
          expanded: new Set(_form.expanded).add(step),
        };
      });
    },
    [form.selectedContacts],
  );

  useEffect(() => {
    if (selectedContacts?.length > 0) {
      let _contactsBreakdown = [];
      setForm((_form) => ({ ..._form, fetchingContactsBreakdown: true }));
      if (form.type === "simioSegment") {
        setForm((_form) => {
          return {
            ..._form,
            // For Simio Segment, don't override contactsBreakdown (it was already set in getSimioSegmentContacts response)
            contactsBreakdown: _form.contactsBreakdown,
            fetchingContactsBreakdown: false,
            pacing: {
              ..._form.pacing,
              effectiveSelectedContacts: _form.selectedContacts || [],
            },
          };
        });
      } else {
        api
          .getContactsBreakdown({
            contactList: selectedContacts.map(
              ({ userPhoneNumber }) => userPhoneNumber,
            ),
          })
          .then((response) => {
            _contactsBreakdown = response;
            setForm((_form) => {
              return {
                ..._form,
                contactsBreakdown: _contactsBreakdown,
              };
            });
          })
          .finally(() => {
            setForm((_form) => {
              const { optins, notOptouts } = _form.contactsBreakdown || {
                optins: [],
                notOptouts: [],
              };
              return {
                ..._form,
                optinsContacts: optins,
                notOutputsContacts: notOptouts,
                fetchingContactsBreakdown: false,
                pacing: {
                  ..._form.pacing,
                  effectiveSelectedContacts: _form.sendOnlyToOptins
                    ? optins
                    : notOptouts,
                },
              };
            });
          });
      }
    } else {
      setForm((_form) => ({
        ..._form,
        optinsContacts: [],
        notOutputsContacts: [],
        contactsBreakdown: null,
        pacing: {
          ..._form.pacing,
          effectiveSelectedContacts: [],
        },
      }));
    }
  }, [form.selectedContacts]);

  const updateSelectedOptions = useCallback(
    (selectedOptions) => {
      setForm((_form) => {
        return { ..._form, selectedOptions };
      });
    },
    [form.selectedOptions],
  );

  const updateInputValue = useCallback(
    (inputValue) => {
      setForm((_form) => {
        return { ..._form, inputValue };
      });
    },
    [form.inputValue],
  );

  // Klaviyo
  const [optionsKlaviyo, setOptionsKlaviyo] = useState();
  const deselectedOptionsKlaviyo = useMemo(() => {
    let segments;
    if (form.segmentsKlaviyo == null) {
      segments = [];
    } else {
      segments = form.segmentsKlaviyo?.map((segment) => {
        return {
          value: segment.id,
          label: segment.attributes.name,
        };
      });
    }
    setOptionsKlaviyo(segments);
    return segments;
  }, [form.segmentsKlaviyo]);

  const updateTextKlaviyo = useCallback(
    (e) => {
      const value = e.target ? e.target.value : e;
      updateInputValue(value);

      if (value === "") {
        setOptionsKlaviyo(deselectedOptionsKlaviyo);
        return;
      }

      const optionsFiltered = deselectedOptionsKlaviyo.filter((option) =>
        (option.label || "")
          .toLowerCase()
          .includes((value || "").toLowerCase()),
      );

      setOptionsKlaviyo(optionsFiltered);
    },
    [deselectedOptionsKlaviyo],
  );

  const updateSelectionKlaviyo = useCallback(
    (selected) => {
      const segmentId = [selected.value];

      const matchedOption = optionsKlaviyo.find((option) => {
        return option.value.match(selected.value);
      });

      updateSelectedOptions(segmentId);
      updateInputValue(matchedOption ? matchedOption.label : "");

      if (segmentId && segmentId.length > 0) {
        showLoader(true);
        api
          .getKlaviyoProfiles({
            segmentId: segmentId[0],
            customerId,
          })
          .then((response) => {
            updateSelectedContacts(
              (response.phoneNumbers || [])
                .filter((i) => i)
                .map((userPhoneNumber) => ({ userPhoneNumber })),
            );
            updateKlaviyoSegmentId(response.contactListId);
          })
          .catch(() => {
            console.error("Erreur lors de l'appel API Klaviyo");
          })
          .finally(() => {
            showLoader(false);
          });
      }
    },
    [optionsKlaviyo],
  );

  // Brevo
  const [optionsBrevo, setOptionsBrevo] = useState();
  const deselectedOptionsBrevo = useMemo(() => {
    let segments;
    if (form.segmentsBrevo == null) {
      segments = [];
    } else {
      segments = form.segmentsBrevo?.map((segment) => {
        return {
          value: segment.id,
          label: segment.segmentName,
        };
      });
    }
    setOptionsBrevo(segments);
    return segments;
  }, [form.segmentsBrevo]);

  const updateTextBrevo = useCallback(
    (e) => {
      const value = e.target ? e.target.value : e;
      updateInputValue(value);

      if (value === "") {
        setOptionsBrevo(deselectedOptionsBrevo);
        return;
      }

      const optionsFiltered = deselectedOptionsBrevo.filter((option) =>
        (option.label || "")
          .toLowerCase()
          .includes((value || "").toLowerCase()),
      );

      setOptionsBrevo(optionsFiltered);
    },
    [deselectedOptionsBrevo],
  );

  const updateSelectionBrevo = useCallback(
    (selected) => {
      const segmentId = [selected.value];
      const matchedOption = optionsBrevo.find((option) => {
        return option.value === selected.value;
      });

      updateSelectedOptions(segmentId);
      updateInputValue(matchedOption ? matchedOption.label : "");

      if (segmentId && segmentId.length > 0) {
        showLoader(true);
        api
          .getBrevoProfiles({
            segmentId: segmentId[0],
            customerId,
          })
          .then((response) => {
            updateSelectedContacts(
              (response.phoneNumbers || [])
                .filter((i) => i)
                .map((userPhoneNumber) => ({ userPhoneNumber })),
            );
            updateBrevoSegmentId(response.contactsegmentId);
          })
          .catch(() => {
            console.error("Error fetching Brevo profiles");
          })
          .finally(() => {
            showLoader(false);
          });
      }
    },
    [optionsBrevo],
  );

  // Simio Segment
  const [optionsSimioSegment, setOptionsSimioSegment] = useState();
  const deselectedOptionsSimioSegment = useMemo(() => {
    let segments = [];

    // Add "All contacts" as the first option
    segments.push({
      value: "all-contacts",
      label: "All contacts",
    });

    // Add the actual segments
    if (form.segmentsSimioSegment != null) {
      const actualSegments = form.segmentsSimioSegment?.map((segment) => {
        return {
          value: segment.id,
          label: `${segment.name} (~${segment.lastContactsCount || 0} contacts)`,
        };
      });
      segments = segments.concat(actualSegments);
    }

    setOptionsSimioSegment(segments);
    return segments;
  }, [form.segmentsSimioSegment]);

  const updateTextSimioSegment = useCallback(
    (e) => {
      const value = e.target ? e.target.value : e;
      updateInputValue(value);

      if (value === "") {
        setOptionsSimioSegment(deselectedOptionsSimioSegment);
        return;
      }

      const optionsFiltered = deselectedOptionsSimioSegment.filter((option) =>
        (option.label || "")
          .toLowerCase()
          .includes((value || "").toLowerCase()),
      );

      setOptionsSimioSegment(optionsFiltered);
    },
    [deselectedOptionsSimioSegment],
  );

  const updateSelectionSimioSegment = useCallback(
    (selected) => {
      const segmentId = [selected.value];

      const matchedOption = optionsSimioSegment.find((option) => {
        return option.value.match(selected.value);
      });

      updateSelectedOptions(segmentId);
      // Extract just the segment name from the label (remove contact count)
      const segmentName = matchedOption
        ? matchedOption.label.split(" (~")[0]
        : "";
      updateInputValue(segmentName);

      if (segmentId && segmentId.length > 0) {
        showLoader(true);
        api
          .getSimioSegmentContacts({
            segmentId: segmentId[0],
            customerId,
          })
          .then((response) => {
            updateSelectedContacts(
              (response.contacts || [])
                .filter((i) => i)
                .map((contact) => ({
                  userPhoneNumber: contact.userPhoneNumber,
                })),
            );
            updateSimioSegmentId(segmentId[0]);

            // Set contactsBreakdown directly from response (no additional API call needed)
            setForm((_form) => ({
              ..._form,
              contactsBreakdown: response.contactsBreakdown,
            }));
          })
          .catch(() => {
            console.error("Error fetching Simio Segment contacts");
          })
          .finally(() => {
            showLoader(false);
          });
      }
    },
    [optionsSimioSegment],
  );

  // Shopify
  const [optionsShopify, setOptionsShopify] = useState();
  const deselectedOptionsShopify = useMemo(() => {
    let segments;
    if (form.segmentsShopify == null) {
      segments = [];
    } else {
      segments = form.segmentsShopify?.map((segment) => {
        return {
          value: segment.query,
          label: segment.name,
        };
      });
    }
    setOptionsShopify(segments);
    return segments;
  }, [form.segmentsShopify]);

  const updateTextShopify = useCallback(
    (e) => {
      const value = e.target ? e.target.value : e;
      updateInputValue(value);

      if (value === "") {
        setOptionsShopify(deselectedOptionsShopify);
        return;
      }

      const optionsFiltered = deselectedOptionsShopify.filter((option) =>
        (option.label || "")
          .toLowerCase()
          .includes((value || "").toLowerCase()),
      );

      setOptionsShopify(optionsFiltered);
    },
    [deselectedOptionsShopify],
  );

  const updateSelectionShopify = useCallback(
    (selected) => {
      const segmentQuery = [selected.value];

      const matchedOption = optionsShopify.find((option) => {
        return option.value.match(selected.value);
      });

      updateSelectedOptions(segmentQuery);
      updateInputValue(matchedOption ? matchedOption.label : "");

      if (segmentQuery && segmentQuery.length > 0) {
        showLoader(true);
        api
          .getShopifySegmentCustomers({
            segmentQuery: segmentQuery[0],
          })
          .then((response) => {
            updateSelectedContacts(
              (response || [])
                .filter((i) => i)
                .map((userPhoneNumber) => ({ userPhoneNumber })),
            );
          })
          .catch(() => {
            console.error("Erreur lors de l’appel API Shopify");
          })
          .finally(() => {
            showLoader(false);
          });
      }
    },
    [optionsShopify],
  );

  // Hubspot
  const [optionsHubspot, setOptionsHubspot] = useState();
  const deselectedOptionsHubspot = useMemo(() => {
    let lists;
    if (form.listsHubspot == null) {
      lists = [];
    } else {
      lists = form.listsHubspot?.map((list) => {
        return {
          value: list.listId,
          label: list.name,
        };
      });
    }
    setOptionsHubspot(lists);
    return lists;
  }, [form.listsHubspot]);

  const updateTextHubspot = useCallback(
    (e) => {
      const value = e.target ? e.target.value : e;
      updateInputValue(value);

      if (value === "") {
        setOptionsHubspot(deselectedOptionsHubspot);
        return;
      }

      const optionsFiltered = deselectedOptionsHubspot.filter((option) =>
        (option.label || "")
          .toLowerCase()
          .includes((value || "").toLowerCase()),
      );

      setOptionsHubspot(optionsFiltered);
    },
    [deselectedOptionsHubspot],
  );

  const updateSelectionHubspot = useCallback(
    (selected) => {
      const listId = [selected.value];

      const matchedOption = optionsHubspot.find((option) => {
        return option.value.match(selected.value);
      });

      updateSelectedOptions(listId);
      updateInputValue(matchedOption ? matchedOption.label : "");

      if (listId && listId.length > 0) {
        showLoader(true);
        api
          .getHubspotContacts({
            listId: listId[0],
            apiKeyHubspot,
            customerId: customerId,
          })
          .then((response) => {
            updateSelectedContacts(
              (response.phoneNumbers || [])
                .filter((i) => i)
                .map((userPhoneNumber) => ({ userPhoneNumber })),
            );
            updateHubspotListId(response.contactListId);
          })
          .catch(() => {
            throw new Error("Erreur lors de l’appel API HubSpot");
          })
          .finally(() => {
            showLoader(false);
          });
      }
    },
    [optionsHubspot],
  );

  const buttonClassList =
    "border-[1px] border-gray-300 bg-transparent text-gray-800 hover:bg-gray-200 hover:text-gray-900 transition duration-200";

  return (
    <>
      <Field
        className={clsx(
          "border rounded-customBorderRadius p-3 mt-5",
          className,
        )}
      >
        <Text className="font-bold">1. Recipients</Text>
        <ButtonGroup className="mt-4 mb-2">
          <Button
            disabled={!integrations.klaviyo || !optionsKlaviyo?.length}
            onClick={() => updateSegmentType(campaignSegmentType.klaviyo)}
            type="button"
            color="alternative"
            className={clsx(
              buttonClassList,
              "flex items-center",
              form.type === campaignSegmentType.klaviyo &&
                "bg-gray-200 text-gray-900",
            )}
          >
            Klaviyo
          </Button>
          <Button
            disabled={!integrations.brevo || !optionsBrevo?.length}
            onClick={() => updateSegmentType(campaignSegmentType.brevo)}
            type="button"
            color="alternative"
            className={clsx(
              buttonClassList,
              "flex items-center",
              form.type === campaignSegmentType.brevo &&
                "bg-gray-200 text-gray-900",
            )}
          >
            Brevo
          </Button>
          <Button
            disabled={!integrations.shopify || !optionsShopify?.length}
            onClick={() => updateSegmentType(campaignSegmentType.shopify)}
            type="button"
            color="alternative"
            className={clsx(
              buttonClassList,
              "flex items-center",
              form.type === campaignSegmentType.shopify &&
                "bg-gray-200 text-gray-900",
            )}
          >
            Shopify
          </Button>
          <Button
            disabled={!integrations.hubspot || !optionsHubspot?.length}
            onClick={() => updateSegmentType(campaignSegmentType.hubspot)}
            type="button"
            color="alternative"
            className={clsx(
              buttonClassList,
              "border-y-[1px] flex items-center",
              form.type === campaignSegmentType.hubspot &&
                "bg-gray-200 text-gray-900",
            )}
          >
            Hubspot
          </Button>
          <Button
            disabled={!optionsSimioSegment?.length}
            onClick={() => updateSegmentType(campaignSegmentType.simioSegment)}
            type="button"
            color="alternative"
            className={clsx(
              buttonClassList,
              form.type === campaignSegmentType.simioSegment &&
                "bg-gray-200 text-gray-900",
            )}
          >
            <span className="flex items-center gap-2">
              Simio Segment
              <Badge color="lime">new</Badge>
            </span>
          </Button>
        </ButtonGroup>

        {form.fetchingContacts ? (
          <div className="flex justify-center mt-2">
            <LoadingSpinner />
          </div>
        ) : form.type === campaignSegmentType.klaviyo ? (
          <Field>
            <Label>Klaviyo segment</Label>
            <AutocompleteInput
              value={form.inputValue}
              onChange={updateTextKlaviyo}
              options={optionsKlaviyo}
              selected={form.selectedOptions}
              onSelect={updateSelectionKlaviyo}
            />
          </Field>
        ) : form.type === campaignSegmentType.brevo ? (
          <Field>
            <Label>Brevo segment</Label>
            <AutocompleteInput
              value={form.inputValue}
              onChange={updateTextBrevo}
              options={optionsBrevo}
              selected={form.selectedOptions}
              onSelect={updateSelectionBrevo}
            />
          </Field>
        ) : form.type === campaignSegmentType.shopify ? (
          <Field>
            <Label>Shopify segment</Label>
            <AutocompleteInput
              value={form.inputValue}
              onChange={updateTextShopify}
              options={optionsShopify}
              selected={form.selectedOptions}
              onSelect={updateSelectionShopify}
            />
          </Field>
        ) : form.type === campaignSegmentType.hubspot ? (
          <Field>
            <Label>Hubspot list</Label>
            <AutocompleteInput
              value={form.inputValue}
              onChange={updateTextHubspot}
              options={optionsHubspot}
              selected={form.selectedOptions}
              onSelect={updateSelectionHubspot}
            />
          </Field>
        ) : form.type === campaignSegmentType.simioSegment ? (
          <Field>
            <Label>Simio segment</Label>
            <AutocompleteInput
              value={form.inputValue}
              onChange={updateTextSimioSegment}
              options={optionsSimioSegment}
              selected={form.selectedOptions}
              onSelect={updateSelectionSimioSegment}
            />
            <div className="mt-2">
              <button
                onClick={() => navigate("/contacts")}
                className="text-pink hover:underline text-sm bg-transparent border-none p-0 cursor-pointer"
              >
                Manage segments
              </button>
            </div>
          </Field>
        ) : (
          <p className="mt-5">Choose a segment source</p>
        )}
        {selectedContacts && form.type && (
          <Text className="mt-2">
            <u>
              <b>{selectedContacts.length}</b> unique phone numbers
            </u>{" "}
            found
          </Text>
        )}
      </Field>
    </>
  );
};

export default SendCampaignRecipients;
